/**
 * Sistema (só administrador):
 * 1. Usuários: criar (com senha provisória, mostrada uma vez para mandar à
 *    pessoa), mudar nome e papel, desativar, gerar nova senha provisória.
 * 2. Saúde do servidor: versão, PHP, SQLite, limites, pasta de dados, migrações.
 * 3. Cópias do banco: as cópias automáticas e "baixar uma cópia agora".
 * 4. Alterações: quem mudou o quê nos cadastros, metas e usuários.
 *
 * Lê: api.usuarios e api.sistema.
 * Usado por: App.tsx (rota #/sistema).
 */
import { useEffect, useState } from 'react';
import { Aviso, ErroNaTela } from '../componentes/basicos.tsx';
import { api, ErroApi, type Alteracao, type InfoDoSistema, type UsuarioDoSistema } from '../dados/api.ts';
import type { Usuario } from '../dominio/tipos.ts';
import { formatarDataHora } from '../formatacao.ts';
import { VERSAO_DO_SITE } from '../versao.ts';

const comoErro = (falha: unknown) => (falha instanceof ErroApi ? falha : new ErroApi('APP', String(falha), 0, null));

export function Sistema({ usuario }: { usuario: Usuario }) {
  const [info, setInfo] = useState<InfoDoSistema | null>(null);
  const [erro, setErro] = useState<ErroApi | null>(null);

  useEffect(() => {
    api.sistema().then(setInfo).catch((falha: unknown) => setErro(comoErro(falha)));
  }, []);

  return (
    <div className="pagina">
      <div className="pagina-titulo">
        <div>
          <h1>Sistema</h1>
          <p>Usuários, saúde do servidor, cópias do banco e o histórico de alterações.</p>
        </div>
      </div>
      <Usuarios eu={usuario} />
      {erro ? <ErroNaTela erro={erro} /> : null}
      {info ? (
        <>
          <Saude info={info} />
          <Copias info={info} />
          <Alteracoes alteracoes={info.alteracoes} />
        </>
      ) : !erro ? (
        <p className="texto-apagado">Carregando…</p>
      ) : null}
    </div>
  );
}

function Usuarios({ eu }: { eu: Usuario }) {
  const [usuarios, setUsuarios] = useState<UsuarioDoSistema[] | null>(null);
  const [erro, setErro] = useState<ErroApi | null>(null);
  const [novo, setNovo] = useState<{ nome: string; email: string; papel: 'gerente' | 'admin' } | null>(null);
  const [editando, setEditando] = useState<UsuarioDoSistema | null>(null);
  /** A senha provisória acabada de criar (aparece uma vez só). */
  const [senha, setSenha] = useState<{ nome: string; email: string; senha: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    api
      .usuarios()
      .then(({ usuarios }) => setUsuarios(usuarios))
      .catch((falha: unknown) => setErro(comoErro(falha)));
  }, []);

  async function executar(acao: () => Promise<void>) {
    setErro(null);
    setOcupado(true);
    try {
      await acao();
    } catch (falha) {
      setErro(comoErro(falha));
    } finally {
      setOcupado(false);
    }
  }

  const criar = () =>
    executar(async () => {
      if (!novo) return;
      const resposta = await api.criarUsuario(novo);
      setUsuarios(resposta.usuarios);
      setSenha({ nome: novo.nome, email: novo.email, senha: resposta.senhaProvisoria });
      setNovo(null);
    });

  const salvar = () =>
    executar(async () => {
      if (!editando) return;
      setUsuarios((await api.salvarUsuario({ id: editando.id, nome: editando.nome, papel: editando.papel, ativo: editando.ativo })).usuarios);
      setEditando(null);
    });

  const redefinir = (u: UsuarioDoSistema) => {
    if (!window.confirm(`Criar uma senha provisória nova para ${u.nome}? A senha atual dela deixa de valer e ela sai do sistema nos aparelhos abertos.`)) return;
    void executar(async () => {
      const resposta = await api.redefinirSenha(u.id);
      setUsuarios(resposta.usuarios);
      setSenha({ nome: u.nome, email: u.email, senha: resposta.senhaProvisoria });
    });
  };

  const endereco = window.location.origin;

  return (
    <section className="cartao pilha">
      <div className="pagina-titulo">
        <div>
          <h2>Usuários</h2>
          <p className="subtitulo">
            Quem entra no sistema. Usuário novo recebe uma senha provisória e cria a dele no primeiro acesso. Administrador também cuida dos usuários e vê esta
            tela.
          </p>
        </div>
        <button type="button" className="botao-secundario" onClick={() => setNovo({ nome: '', email: '', papel: 'gerente' })} disabled={novo !== null}>
          Novo usuário
        </button>
      </div>
      {erro ? <ErroNaTela erro={erro} /> : null}
      {senha ? (
        <Aviso tipo="ok">
          Senha provisória de {senha.nome}: <strong className="senha-provisoria">{senha.senha}</strong> (aparece só agora). Mande para a pessoa, por exemplo:
          <span className="mensagem-pronta">
            Acesse {endereco} · e-mail: {senha.email} · senha provisória: {senha.senha} · no primeiro acesso o sistema pede para você criar a sua senha.
          </span>
          <button type="button" className="botao-link" onClick={() => setSenha(null)}>
            já mandei, esconder
          </button>
        </Aviso>
      ) : null}
      {novo ? (
        <div className="antes-depois">
          <label>
            Nome
            <input value={novo.nome} onChange={(e) => setNovo({ ...novo, nome: e.target.value })} />
          </label>
          <label>
            E-mail
            <input type="email" value={novo.email} onChange={(e) => setNovo({ ...novo, email: e.target.value })} />
          </label>
          <label>
            Papel
            <select value={novo.papel} onChange={(e) => setNovo({ ...novo, papel: e.target.value as 'gerente' | 'admin' })}>
              <option value="gerente">Gerente (usa o sistema)</option>
              <option value="admin">Administrador (também cuida dos usuários)</option>
            </select>
          </label>
          <button type="button" className="botao" onClick={() => void criar()} disabled={ocupado || novo.nome.trim() === '' || novo.email.trim() === ''}>
            Criar e gerar senha provisória
          </button>
          <button type="button" className="botao-secundario" onClick={() => setNovo(null)}>
            Cancelar
          </button>
        </div>
      ) : null}
      {editando ? (
        <div className="antes-depois">
          <label>
            Nome
            <input value={editando.nome} onChange={(e) => setEditando({ ...editando, nome: e.target.value })} />
          </label>
          <label>
            Papel
            <select value={editando.papel} onChange={(e) => setEditando({ ...editando, papel: e.target.value as 'gerente' | 'admin' })} disabled={editando.id === eu.id}>
              <option value="gerente">Gerente</option>
              <option value="admin">Administrador</option>
            </select>
          </label>
          <label className="campo-checagem">
            <input type="checkbox" checked={editando.ativo} onChange={(e) => setEditando({ ...editando, ativo: e.target.checked })} disabled={editando.id === eu.id} />
            Pode entrar (desmarque para tirar o acesso)
          </label>
          <button type="button" className="botao" onClick={() => void salvar()} disabled={ocupado || editando.nome.trim() === ''}>
            Salvar
          </button>
          <button type="button" className="botao-secundario" onClick={() => setEditando(null)}>
            Cancelar
          </button>
        </div>
      ) : null}
      {usuarios ? (
        <div className="tabela-rolavel">
          <table className="tabela">
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Papel</th>
                <th>Situação</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.nome}
                    {u.id === eu.id ? <span className="texto-apagado"> (você)</span> : null}
                  </td>
                  <td>{u.email}</td>
                  <td>{u.papel === 'admin' ? 'Administrador' : 'Gerente'}</td>
                  <td>
                    {!u.ativo ? (
                      <span className="etiqueta etiqueta-apagada">Sem acesso</span>
                    ) : u.senhaProvisoria ? (
                      <span className="etiqueta etiqueta-alerta">Ainda não criou a senha</span>
                    ) : (
                      <span className="etiqueta etiqueta-bom">Ativo</span>
                    )}
                  </td>
                  <td>
                    <div className="linha-campos">
                      <button type="button" className="botao-secundario" onClick={() => setEditando(u)}>
                        Editar
                      </button>
                      {u.id !== eu.id ? (
                        <button type="button" className="botao-secundario" onClick={() => redefinir(u)} disabled={ocupado || !u.ativo}>
                          Nova senha provisória
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !erro ? (
        <p className="texto-apagado">Carregando…</p>
      ) : null}
    </section>
  );
}

const megabytes = (bytes: number) => `${(bytes / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;

function Saude({ info }: { info: InfoDoSistema }) {
  const c = info.banco.contagens;
  return (
    <section className="cartao pilha">
      <h2>Saúde do servidor</h2>
      {info.pastaDeDados.foraDoSite === false || !info.pastaDeDados.gravavel ? (
        <Aviso tipo="erro">A pasta de dados precisa ficar fora do site e aceitar gravação. Confira o log do servidor.</Aviso>
      ) : null}
      {info.versao !== VERSAO_DO_SITE ? (
        <Aviso tipo="alerta">
          A versão desta tela ({VERSAO_DO_SITE}) é diferente da do servidor ({info.versao}). Recarregue a página (Ctrl+Shift+R).
        </Aviso>
      ) : null}
      <div className="lista-numeros">
        <div>
          <span>Versão</span>
          <strong>{info.versao}</strong>
        </div>
        <div>
          <span>PHP · SQLite</span>
          <strong>
            {info.php} · {info.sqlite}
          </strong>
        </div>
        <div>
          <span>Banco</span>
          <strong>{megabytes(info.banco.tamanho)}</strong>
        </div>
        <div>
          <span>Vendas guardadas</span>
          <strong>{c['vendas']?.toLocaleString('pt-BR')}</strong>
        </div>
        <div>
          <span>Importações · relatos abertos</span>
          <strong>
            {c['importacoes']} · {c['relatosAbertos']}
          </strong>
        </div>
      </div>
      <p className="texto-pequeno texto-apagado">
        Pasta de dados fora do site: {info.pastaDeDados.foraDoSite === null ? 'não se aplica (computador)' : info.pastaDeDados.foraDoSite ? 'sim' : 'NÃO'} · gravação: {info.pastaDeDados.gravavel ? 'ok' : 'NÃO'} · limites do PHP:{' '}
        {Object.entries(info.limites)
          .map(([nome, valor]) => `${nome} ${valor}`)
          .join(', ')}{' '}
        · migrações: {info.banco.migracoes.map((m) => `${m.nome} (${formatarDataHora(m.aplicadaEm)})`).join(', ')}
      </p>
    </section>
  );
}

function Copias({ info }: { info: InfoDoSistema }) {
  return (
    <section className="cartao pilha">
      <div className="pagina-titulo">
        <div>
          <h2>Cópias do banco</h2>
          <p className="subtitulo">
            Uma cópia por dia, sozinha (guarda as 30 mais novas), e uma antes de cada mudança na estrutura do banco. Ficam no servidor, fora do site. A cópia
            baixada tem as vendas: guarde com cuidado.
          </p>
        </div>
        <a className="botao-secundario" href="/api/sistema.php?baixar=banco" download>
          Baixar uma cópia agora
        </a>
      </div>
      {info.copias.length === 0 ? (
        <p className="texto-apagado">Nenhuma cópia ainda.</p>
      ) : (
        <ul className="lista-ligacoes">
          {info.copias.slice(0, 12).map((copia) => (
            <li key={copia.arquivo}>
              {copia.arquivo} <span className="texto-apagado">({megabytes(copia.tamanho)})</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const NOME_DA_ACAO: Record<string, string> = {
  instalarSistema: 'Primeiro acesso (administrador criado)',
  criarUsuarioPeloTerminal: 'Usuário criado pelo terminal',
  criarUsuario: 'Usuário criado',
  salvarUsuario: 'Usuário alterado',
  redefinirSenha: 'Senha provisória nova',
  trocarMinhaSenha: 'Trocou a própria senha',
  salvarRepresentante: 'Representante salvo',
  excluirRepresentante: 'Representante excluído',
  trocarRepresentante: 'Saiu da equipe (troca de representante)',
  vincularCodigo: 'Código ligado',
  desvincularCodigo: 'Ligação de código removida',
  salvarMarca: 'Marca salva',
  salvarFeriado: 'Feriado salvo',
  removerFeriado: 'Feriado removido',
  salvarImagem: 'Foto ou logo trocada',
  removerImagem: 'Foto ou logo removida',
  salvarMetas: 'Metas salvas',
};

/** O detalhe em uma linha ("sai: Fabricia · entra: Tatielly · desde: 2026-09"). */
function resumir(detalhe: Record<string, unknown>): string {
  return Object.entries(detalhe)
    .filter(([, valor]) => valor !== null && valor !== '' && !(Array.isArray(valor) && valor.length === 0))
    .map(([chave, valor]) => `${chave}: ${Array.isArray(valor) ? valor.join('; ') : String(valor)}`)
    .join(' · ');
}

function Alteracoes({ alteracoes }: { alteracoes: Alteracao[] }) {
  return (
    <section className="cartao pilha">
      <div>
        <h2>Alterações</h2>
        <p className="subtitulo">Quem mudou o quê nos cadastros, metas e usuários (as 300 mais recentes). Importações e carteiras ficam em Importar → Histórico.</p>
      </div>
      {alteracoes.length === 0 ? (
        <p className="texto-apagado">Nenhuma alteração ainda.</p>
      ) : (
        <div className="tabela-rolavel">
          <table className="tabela">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Quem</th>
                <th>O quê</th>
                <th>Detalhe</th>
              </tr>
            </thead>
            <tbody>
              {alteracoes.map((a) => (
                <tr key={a.id}>
                  <td className="numero">{formatarDataHora(a.momento)}</td>
                  <td>{a.usuario ?? 'terminal'}</td>
                  <td>{NOME_DA_ACAO[a.acao] ?? a.acao}</td>
                  <td className="texto-pequeno">{resumir(a.detalhe)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
